import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface AssinaturaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (jobsPerMonth: number) => Promise<void>;
  isLoading: boolean;
}

const assinaturaOptions = [
  { vagas: 5, valor: "R$ 220,00" },
  { vagas: 10, valor: "R$ 275,00" },
  { vagas: 20, valor: "R$ 330,00" },
  { vagas: 30, valor: "R$ 415,00" },
  { vagas: 50, valor: "R$ 550,00" },
  { vagas: 100, valor: "R$ 880,00" },
  { vagas: 200, valor: "R$ 1.500,00" },
];

export function AssinaturaModal({ open, onOpenChange, onConfirm, isLoading }: AssinaturaModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<number>(5);
  const isMobile = useIsMobile();

  const handleConfirm = async () => {
    await onConfirm(selectedPlan);
  };

  const ModalContent = () => (
    <RadioGroup
      value={selectedPlan.toString()}
      onValueChange={(value) => setSelectedPlan(Number(value))}
      className="space-y-3 py-4 max-h-[400px] overflow-y-auto"
    >
      {assinaturaOptions.map((option) => (
        <div
          key={option.vagas}
          className="flex items-center space-x-3 rounded-lg border p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        >
          <RadioGroupItem value={option.vagas.toString()} id={`plan-${option.vagas}`} />
          <Label
            htmlFor={`plan-${option.vagas}`}
            className="flex-1 cursor-pointer flex justify-between items-center"
          >
            <span className="font-medium">
              {option.vagas} vagas/mês
            </span>
            <span className="text-muted-foreground">
              {option.valor}/mês
            </span>
          </Label>
        </div>
      ))}
    </RadioGroup>
  );

  const ModalActions = () => (
    <div className="flex justify-end gap-3">
      <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
        Cancelar
      </Button>
      <Button onClick={handleConfirm} disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processando...
          </>
        ) : (
          "Ir para Pagamento"
        )}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>Assinar Plano Profissional</DrawerTitle>
            <DrawerDescription>
              Selecione o plano ideal para sua empresa. Faturamento mensal recorrente.
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 max-h-[50vh] overflow-y-auto">
            <ModalContent />
          </div>
          <DrawerFooter>
            <ModalActions />
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assinar Plano Profissional</DialogTitle>
          <DialogDescription>
            Selecione o plano ideal para sua empresa. Faturamento mensal recorrente.
          </DialogDescription>
        </DialogHeader>
        <ModalContent />
        <ModalActions />
      </DialogContent>
    </Dialog>
  );
}
